import { createTheme } from '@mui/material/styles';

const theme = createTheme({
  palette: {
    mode: 'light',
    primary: {
      main: '#8B7D6B', // Een zachtere, elegantere bruintint
      light: '#A89985',
      dark: '#6A5D4B',
      contrastText: '#FFFFFF',
    },
    secondary: {
      main: '#D0A980', // Warmere accentkleur (goud/champagne)
      light: '#E0C3A3',
      dark: '#B78F66',
      contrastText: '#FFFFFF',
    },
    background: {
      default: '#F9F5F0', // Zeer zachte crèmetint voor achtergrond (bijna wit)
      paper: '#FFFFFF',
    },
    text: {
      primary: '#4A4A4A', // Zachtere donkergrijstint voor hoofdtekst
      secondary: '#7A7A7A', // Zachter grijs voor subtekst
    },
  },
  typography: {
    fontFamily: '"Cormorant Garamond", "Poppins", "Roboto", "Helvetica", "Arial", sans-serif',
    h1: {
      fontFamily: '"Cormorant Garamond", serif',
      fontWeight: 600,
      fontSize: '3.2rem',
      letterSpacing: '0.5px',
    },
    h2: {
      fontFamily: '"Cormorant Garamond", serif',
      fontWeight: 600,
      fontSize: '2.4rem',
      letterSpacing: '0.5px',
    },
    h3: {
      fontFamily: '"Cormorant Garamond", serif',
      fontWeight: 600,
      fontSize: '1.8rem',
      letterSpacing: '0.5px',
    },
    h4: {
      fontFamily: '"Cormorant Garamond", serif',
      fontWeight: 600,
      fontSize: '1.5rem',
    },
    h5: {
      fontFamily: '"Poppins", sans-serif',
      fontWeight: 500,
      fontSize: '1.2rem',
      letterSpacing: '1px',
      textTransform: 'uppercase',
    },
    h6: {
      fontFamily: '"Poppins", sans-serif',
      fontWeight: 500,
      fontSize: '1rem',
      letterSpacing: '1px',
      textTransform: 'uppercase',
    },
    body1: {
      fontFamily: '"Poppins", sans-serif',
      fontSize: '1rem',
      lineHeight: 1.8,
    },
    body2: {
      fontFamily: '"Poppins", sans-serif',
      fontSize: '0.9rem',
      lineHeight: 1.8,
    },
    button: {
      fontFamily: '"Poppins", sans-serif',
      fontWeight: 500,
      fontSize: '0.9rem',
      letterSpacing: '1px',
      textTransform: 'uppercase',
    },
    subtitle1: {
      fontFamily: '"Cormorant Garamond", serif',
      fontSize: '1.2rem',
      fontStyle: 'italic',
    },
  },
  components: {
    MuiTypography: {
      defaultProps: {
        variantMapping: {
          h1: 'h1',
          h2: 'h2',
          h3: 'h2',
          h4: 'h2',
          h5: 'h2',
          h6: 'h3',
          subtitle1: 'p',
          subtitle2: 'p',
          body1: 'p',
          body2: 'p',
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          padding: '12px 28px',
          transition: 'all 0.3s ease',
          textTransform: 'none',
          fontWeight: 500,
        },
        contained: {
          backgroundColor: '#D0A980',
          boxShadow: 'none',
          '&:hover': {
            boxShadow: 'none',
            backgroundColor: '#B78F66',
          },
        },
        outlined: {
          borderWidth: '1px',
          borderColor: '#D0A980',
          color: '#D0A980',
          '&:hover': {
            borderWidth: '1px',
            borderColor: '#B78F66',
            backgroundColor: 'rgba(208, 169, 128, 0.04)',
          },
        },
        text: {
          position: 'relative',
          color: '#D0A980',
          '&::after': {
            content: '""',
            position: 'absolute',
            width: '0%',
            height: '1px',
            bottom: '8px',
            left: '50%',
            transform: 'translateX(-50%)',
            backgroundColor: '#D0A980',
            transition: 'width 0.3s ease',
          },
          '&:hover::after': {
            width: '80%',
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.05)',
          transition: 'transform 0.3s ease, box-shadow 0.3s ease',
          backgroundColor: '#FFFFFF',
          marginBottom: '24px',
          overflow: 'hidden',
          '&:hover': {
            transform: 'translateY(-4px)',
            boxShadow: '0 6px 20px rgba(0, 0, 0, 0.08)',
          },
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        colorSecondary: {
          backgroundColor: '#D0A980',
          color: '#FFFFFF',
        },
        outlinedSecondary: {
          borderColor: '#D0A980',
          color: '#D0A980',
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          backgroundColor: '#FFFFFF',
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.05)',
        },
        elevation1: {
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.05)',
        },
      },
    },
    MuiContainer: {
      styleOverrides: {
        root: {
          paddingLeft: 24,
          paddingRight: 24,
        },
      },
    },
    MuiDivider: {
      styleOverrides: {
        root: {
          margin: '24px 0',
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          boxShadow: 'none',
          borderBottom: '1px solid rgba(0, 0, 0, 0.08)',
          backgroundColor: '#FFFFFF',
        },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          color: '#D0A980',
          '&:hover': {
            backgroundColor: 'rgba(208, 169, 128, 0.04)',
          },
        },
      },
    },
    MuiLink: {
      styleOverrides: {
        root: {
          color: '#D0A980',
          '&:hover': {
            color: '#B78F66',
          },
        },
      },
    },
  },
  shape: {
    borderRadius: 8,
  },
});

// Add custom theme with content box styling
const themeWithContentBox = {
  ...theme,
  contentBox: {
    backgroundColor: '#FFFFFF',
    borderRadius: 8,
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.05)',
    padding: '24px',
    marginBottom: '24px',
  }
};

export { theme as default, themeWithContentBox }; 
